import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { PrimaryPopover, NewSelect,  } from ".";
import { CreateNewFieldStyle, NewFieldStyles, NewFieldTypes, MenuFloats, NewSelectHandles, PopoverPosition } from "../../Definitions";
import NewButton from "./Buttons/newButton";

export const normalizeFeedTime = (minutes: number) => {
  return minutes === 0 ? 5 : minutes;
};

// Time Options
const createSelectOptions = <T extends string,> (values: T[]) => 
  values.map(value => ({ label: value, value }));

// const hours12 = createSelectOptions(["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"])
const hours24 = createSelectOptions(["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "00"])
const minutes = createSelectOptions(["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"])

type Hour = "01"| "02"| "03"| "04"| "05"| "06"| "07"| "08"| "09"| "10"| "11"| "12"| "13"| "14"| "15"| "16"| "17"| "18"| "19"| "20"| "21"| "22"| "23"| "00"
type Minute ="00"| "05"| "10"| "15"| "20"| "25"| "30"| "35"| "40"| "45"| "50"| "55"

export const convertMinutesFromMidnightToHour = (minutesFromMidnight: number): Hour => {
  const hour = Math.floor(minutesFromMidnight/60);
  if (hour > 24) {
    return "00";
  } 
  return String(hour).padStart(2,"0") as Hour;
}

export const convertMinutesFromMidnightToMinute = (minutesFromMidnight: number): Minute => {
  const minutes = minutesFromMidnight % 60;
  if (minutes > 55) {
    return "55";
  } 
  return String(minutes).padStart(2,"0") as Minute;
}

// Commit Btn Style
export const CommitBtnStyle = {light: "light", dark: "dark"} as const

interface newTimePicker_Props {
  header?: string
  popover?: boolean
  background?: boolean
  commitBtn?: boolean
  isDisabled?: boolean
  onTimeSelect?: (time: Date) => void;
  // consider if we would want to handle this imperatively since we do it every where else.
  valueOverrides?: {
    defaultHour?: Hour 
    defaultMinute?: Minute
  }
  overrides?:{
    fieldStyle?: NewFieldStyles
    fieldType?: NewFieldTypes
    commitBtnStyle?: typeof CommitBtnStyle[keyof typeof CommitBtnStyle]
    backgroundColor?: colors
  }
  valueRef?: MutableRefObject<{hour: string | undefined, minute: string|undefined}>
}
/**
 * NewTimePicker
 * @param header Optional title displayed above the picker.
 * @param popover Whether the time picker appears in a popover.
 * @param background Shows the picker with a background container.
 * @param commitBtn Displays a button to confirm the selected time.
 * @param onTimeSelect Callback triggered with the selected time as a Date.
 * @param overrides.fieldStyle Custom style for the time select fields.
 * @param overrides.fieldType Determines the input field type variant.
 * @param overrides.commitBtnStyle Style for the commit button (light or dark).
 * @param overrides.backgroundColor Background color for the container.
 * @returns A styled time selection component.
 */
export default function NewTimePicker({ header, popover = false, background = false, commitBtn = false, isDisabled = false, onTimeSelect, overrides, valueOverrides, valueRef }:newTimePicker_Props) {
  const {t} = useTranslation()
  
  const selectMinuteRef = useRef<NewSelectHandles>(null);
  const selectHourRef = useRef<NewSelectHandles>(null);
  
  useEffect(() => {
    if (valueOverrides?.defaultHour) {
      selectHourRef.current?.setValue({label: valueOverrides.defaultHour, id: valueOverrides.defaultHour});
      return
    }
    
    selectHourRef.current?.setValue(hours24[0]);
  },[valueOverrides])

  useEffect(() => {
    if (valueOverrides?.defaultMinute) {
      selectMinuteRef.current?.setValue({label: valueOverrides.defaultMinute, id: valueOverrides.defaultMinute});
      return
    }
    selectMinuteRef.current?.setValue(minutes[0]);
  },[valueOverrides])


  const handleTimeSelect = () => {
    if (!selectHourRef.current?.value || !selectMinuteRef.current?.value) {
      return;
    }
    const now = new Date();
    const selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parseInt(selectHourRef.current.value.label, 10), parseInt(selectMinuteRef.current.value.label, 10));

    if (onTimeSelect) {
      onTimeSelect(selectedDate);
    }
  };

  // FieldStyle
  const { headerColor, fontColor, primaryColor } = useMemo(() => {
    var _Style = CreateNewFieldStyle(overrides?.fieldStyle)
    return _Style
  }, [overrides?.fieldStyle])

  // More styling
  const _headerShadow = overrides&&overrides.fieldStyle === NewFieldStyles.greyTertiary || overrides&&overrides.fieldStyle === NewFieldStyles.greyLightSecondary ? undefined : {...fonts.textShadow} 
  const _backgroundColor = overrides&&overrides.backgroundColor ? overrides.backgroundColor : primaryColor
  const _commitBtnHeight = ( !(overrides&&overrides.fieldType === NewFieldTypes.tertiaryField) || background ? "30px" : "25px" )

  // Commit Btn Style
  let _commitBtnColor
  let _commitBtnColorHover
  let _commitBtnFontColor
  switch (overrides&&overrides.commitBtnStyle) {
    case CommitBtnStyle.dark:
      _commitBtnColor = colors.greyDarkPrimary
      _commitBtnColorHover = colors.blackPrimary
      _commitBtnFontColor = colors.greyLightTertiary
    break;
    case CommitBtnStyle.light:
    default:
      _commitBtnColor = colors.greyTertiary
      _commitBtnColorHover = colors.greyLightSecondary
      _commitBtnFontColor = colors.greyDarkSecondary
    break;
  }

  // Select Fields
  const Select = ( 
    <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px", cursor: isDisabled ? "not-allowed" : "pointer"}}>
      <NewSelect 
        ref={selectHourRef}
        options={hours24}
        onChange={
          (option) => {
            if (option) {
              if (valueRef) {
                valueRef.current.hour = option.label;
                valueRef.current.minute = selectMinuteRef.current?.value.label;
              }
              if (!commitBtn) handleTimeSelect(); 
            }
          }
        }
        fieldOverrides={{
          fieldWidth: "60px",
          fieldStyle: overrides&&overrides.fieldStyle ? overrides.fieldStyle : popover ? NewFieldStyles.greyDarkSecondary : undefined,
          fieldType: overrides&&overrides.fieldType ? overrides.fieldType : (popover||background) ? NewFieldTypes.tertiaryField : NewFieldTypes.secondaryField,
          menuWidth: background ? "80px" : "134px",
          menuFloat: background ? MenuFloats.center : MenuFloats.left
        }}
        isDisabled={isDisabled}
      />
      <p style={{...fonts.header, ...fonts.fontSize3, color: fontColor}}>:</p>
      <NewSelect 
        ref={selectMinuteRef}
        options={minutes}
        onChange={
          (option) => {
            if (option) {
              if (valueRef) {
                valueRef.current.minute = option.label;
                valueRef.current.hour = selectHourRef.current?.value.label;
              }
              if (!commitBtn) handleTimeSelect(); 
            }
          }
        }
        fieldOverrides={{
          fieldWidth: "60px",
          fieldStyle: overrides&&overrides.fieldStyle ? overrides.fieldStyle : popover ? NewFieldStyles.greyDarkSecondary : undefined,
          fieldType: overrides&&overrides.fieldType ? overrides.fieldType : (popover||background) ? NewFieldTypes.tertiaryField : NewFieldTypes.secondaryField,
          menuWidth: background ? "80px" : "134px",
          menuFloat: background ? MenuFloats.center : MenuFloats.right
        }}
        isDisabled={isDisabled}
      />
    </div> 
  )

  // Commit Btn
  const CommitBtn = (
    <NewButton 
      content={t("useSelectedTime", {defaultValue: "use selected time"})}
      onClick={handleTimeSelect}
      overrides={{
        baseStyles: { width:"fit-content", height: _commitBtnHeight, backgroundColor: _commitBtnColor, padding: "0 10px"},
        hoverStyles: {backgroundColor: _commitBtnColorHover},
        contentStyles: {color: _commitBtnFontColor, textShadow: "none", ...fonts.fontSize4}
      }}
    />
  )

  return ( <>
    { popover 
    ? <PrimaryPopover
        content={<glyphs.Add size="12px"/>}
        popoverHeader="Time Select"
        popoverContent={ 
          <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", padding: "10px"}}>
            {Select}
            {CommitBtn}
          </div> 
        }
        overrides={{
          contentContainer: {width: "fit-content", cursor: "pointer"},
          popoverPosition: PopoverPosition.bottom
        }}
      />
    : <div style={{}}>
        {header && <p style={{ ..._headerShadow, ...fonts.header, ...fonts.fontSize4, color: headerColor, padding: "0px 0px 2px 10px"}}>{header}</p>}
        <div style={{width: "fit-content", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "10px"}}>
          {background ?
          <div style={{borderRadius: "10px", backgroundColor: _backgroundColor, display: "flex", alignItems: "center", justifyContent: "start", height: "30px", width: "fit-content", padding: "0px 10px", boxSizing: "border-box" }}>
            {Select}
          </div>
          : <>{Select}</>}
          {commitBtn ? CommitBtn : <></>}
        </div>
      </div>
    }
  </> );
}